<template>
  <div class="page-layout">
    <Sidebar />
    <div class="assessment-container main-content">
      <div class="assessment-header">
        <h2>Assessment Results</h2>
        <div class="assessment-stats">
          <div class="stat-item">
            <span class="stat-label">Total Responses:</span>
            <span class="stat-value">{{ totalResponses }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Current Page:</span>
            <span class="stat-value">{{ currentPage }}</span>
          </div>
          <button @click="clearAssessments" class="clear-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3-3h8M9 3v3M15 3v3M9 14l6 6M15 14l-6 6"/>
            </svg>
            Clear Data
          </button>
        </div>
      </div>

      <div class="assessment-grid" v-if="assessments.length">
        <div v-for="assessment in assessments" :key="assessment.option_id" class="assessment-card">
          <div class="card-header">
            <h3>{{ assessment.option.value }}</h3>
            <span class="arabic-text">{{ assessment.option.value_ar }}</span>
          </div>
          <div class="card-content">
            <div class="user-info">
              <img 
                :src="assessment.user.img_url" 
                :alt="assessment.user.first_name"
                class="user-avatar"
                @error="handleImageError"
              >
              <div class="user-details">
                <p class="user-name">{{ assessment.user.first_name }} {{ assessment.user.last_name }}</p>
                <p class="user-email">{{ assessment.user.email }}</p>
              </div>
            </div>
            <div class="question-info">
              <p class="question-text">{{ assessment.option.question.question }}</p>
              <p class="part-title">{{ assessment.option.question.part.title }}</p>
              <p class="part-description">{{ assessment.option.question.part.description }}</p>
            </div>
            <div class="meta-info">
              <span class="date">{{ formatDate(assessment.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="no-data">
        <p>No assessment data available</p>
      </div>

      <div class="pagination" v-if="totalPages > 1">
        <button 
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="pagination-btn"
        >
          Previous
        </button>
        
        <div class="page-numbers">
          <button 
            v-for="page in displayedPages" 
            :key="page"
            @click="changePage(page)"
            :class="['page-number', { active: page === currentPage }]"
          >
            {{ page }}
          </button>
        </div>

        <button 
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="pagination-btn"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, onMounted, computed } from 'vue';
import Sidebar from './Sidebar.vue';

export default {
  name: 'AssessmentList',
  components: {
    Sidebar
  },
  setup() {
    const assessments = ref([]);
    const currentPage = ref(1);
    const totalPages = ref(0);
    const totalResponses = ref(0);
    const loading = ref(false);
    const error = ref(null);

    const filters = ref({
      startDate: '',
      endDate: '',
      search: ''
    });

    const fetchAssessments = async (page = 1) => {
      try {
        loading.value = true;
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get(`https://api.kewaar.com/api/admin/assessments?page=${page}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          assessments.value = response.data.data.data;
          totalPages.value = response.data.data.last_page;
          totalResponses.value = response.data.data.total;
          currentPage.value = page;
        }
      } catch (err) {
        error.value = err.message;
        console.error('Error fetching assessments:', err);
      } finally {
        loading.value = false;
      }
    };

    const displayedPages = computed(() => {
      const pages = [];
      const maxVisiblePages = 5;
      
      if (totalPages.value <= maxVisiblePages) {
        for (let i = 1; i <= totalPages.value; i++) {
          pages.push(i);
        }
      } else {
        let start = Math.max(currentPage.value - 2, 1);
        let end = Math.min(start + maxVisiblePages - 1, totalPages.value);
        
        if (end === totalPages.value) {
          start = Math.max(end - maxVisiblePages + 1, 1);
        }
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    });

    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        fetchAssessments(page);
      }
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const handleImageError = (event) => {
      event.target.src = '/default-avatar.png'; // Replace with your default avatar image path
    };

    const applyFilters = () => {
      fetchAssessments(1); // Reset to first page when applying filters
    };

    const clearAssessments = async () => {
      try {
        if (!confirm('Are you sure you want to clear all assessment data? This action cannot be undone.')) {
          return;
        }

        loading.value = true;
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No authentication token found');
        }

        await axios.delete('https://api.kewaar.com/api/admin/assessments', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Clear local state
        assessments.value = [];
        totalResponses.value = 0;
        totalPages.value = 0;
        currentPage.value = 1;

      } catch (err) {
        error.value = err.message;
        console.error('Error clearing assessments:', err);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchAssessments();
    });

    return {
      assessments,
      currentPage,
      totalPages,
      totalResponses,
      loading,
      error,
      displayedPages,
      changePage,
      formatDate,
      handleImageError,
      filters,
      applyFilters,
      clearAssessments
    };
  }
};
</script>

<style scoped>
.page-layout {
  display: flex;
  margin-left: 280px; /* Match sidebar width */
  min-height: 100vh;
}

.main-content {
  flex: 1;
  padding: 2rem;
}

.assessment-container {
  padding: 0;
  margin: 0;
}

.assessment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.assessment-header h2 {
  font-size: 1.8rem;
  color: #2c3e50;
  margin: 0;
}

.assessment-stats {
  display: flex;
  gap: 1.5rem;
}

.stat-item {
  background: #f8fafc;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.stat-label {
  color: #64748b;
  margin-right: 0.5rem;
}

.stat-value {
  font-weight: 600;
  color: #1e293b;
}

.assessment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.assessment-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.assessment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: linear-gradient(135deg, #2A2F3C 0%, #373d4b 100%);
  padding: 1.25rem;
  color: white;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.arabic-text {
  font-size: 0.9rem;
  opacity: 0.9;
}

.card-content {
  padding: 1.25rem;
}

.user-info {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 1rem;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: 600;
  margin: 0;
  color: #1e293b;
}

.user-email {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0 0;
}

.question-info {
  margin: 1rem 0;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
}

.question-text {
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 0.5rem;
}

.part-title {
  font-weight: 600;
  color: #2c3e50;
  margin: 0.5rem 0;
}

.part-description {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

.meta-info {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.date {
  font-size: 0.875rem;
  color: #64748b;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  background: #e2e8f0;
}

.page-numbers {
  display: flex;
  gap: 0.5rem;
}

.page-number {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-number.active {
  background: #2A2F3C;
  color: white;
  border-color: #2A2F3C;
}

.page-number:not(.active):hover {
  background: #f8fafc;
}

.no-data {
  text-align: center;
  padding: 3rem;
  background: #f8fafc;
  border-radius: 12px;
  color: #64748b;
}

.clear-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.clear-button:hover {
  background-color: #dc2626;
}

.clear-button svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 768px) {
  .page-layout {
    margin-left: 0;
  }
}
</style> 